import { Image } from 'expo-image';
import { useEffect, useState } from 'react';
import { Pressable, View, type ViewStyle } from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import type { SvgProps } from 'react-native-svg';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { Text } from '@/ui';
import { Icon } from '@/ui/icon';

export interface FeatureCarouselItem {
  title: string;
  body: string;
  icon?: React.ComponentType<SvgProps>;
  iconSize?: number;
  iconImage?: unknown;
  image: unknown;
}

interface FeatureCarouselProps {
  features: readonly FeatureCarouselItem[];
  onIndexChange?: (index: number) => void;
}

const ROTATE_INTERVAL_MS = 5000;
const FADE_DURATION_MS = 250;

export const FeatureCarousel = ({
  features,
  onIndexChange,
}: FeatureCarouselProps) => {
  const { styles, theme } = useStyles(stylesheet);

  const [activeIndex, setActiveIndex] = useState(0);
  const [displayedIndex, setDisplayedIndex] = useState(0);
  const [incomingIndex, setIncomingIndex] = useState<number | null>(null);
  const incomingOpacity = useSharedValue(0);
  const incomingPreviewStyle = useAnimatedStyle(() => ({
    opacity: incomingOpacity.value,
  }));

  const previewUri = (features[displayedIndex].image as { uri: string }).uri;
  const incomingPreviewUri =
    incomingIndex !== null
      ? (features[incomingIndex].image as { uri: string }).uri
      : null;

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((index) => (index + 1) % features.length);
    }, ROTATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [features.length]);

  useEffect(() => {
    onIndexChange?.(activeIndex);
  }, [activeIndex, onIndexChange]);

  useEffect(() => {
    if (activeIndex === displayedIndex) return undefined;

    setIncomingIndex(activeIndex);
    incomingOpacity.value = 0;
    incomingOpacity.value = withTiming(
      1,
      { duration: FADE_DURATION_MS },
      (finished) => {
        if (!finished) return;
        runOnJS(setDisplayedIndex)(activeIndex);
        runOnJS(setIncomingIndex)(null);
      }
    );
  }, [activeIndex, displayedIndex, incomingOpacity]);

  return (
    <View style={styles.carousel}>
      <View style={styles.features}>
        {features.map((feature, index) => {
          const FeatureIcon = feature.icon;

          return (
            <Pressable
              key={feature.title}
              onPress={() => setActiveIndex(index)}
              style={[
                styles.feature,
                activeIndex !== index && styles.featureInactive,
              ]}
            >
              {feature.iconImage ? (
                <Image
                  source={feature.iconImage}
                  style={styles.featureIconImage}
                  contentFit="contain"
                />
              ) : (
                <Icon
                  type="dressed"
                  icon={FeatureIcon ? <FeatureIcon /> : undefined}
                  width={feature.iconSize}
                  height={feature.iconSize}
                  fillColor={theme.colors.icon.nakedColor}
                  style={styles.featureIconBox}
                />
              )}
              <View style={styles.featureCopy}>
                <Text type="title2">{feature.title}</Text>
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {feature.body}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>

      <View style={styles.preview}>
        <View
          style={
            {
              ...styles.previewLayer,
              backgroundImage: `url("${previewUri}")`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            } as ViewStyle
          }
        />

        {incomingPreviewUri ? (
          <Animated.View
            style={[
              {
                ...styles.previewLayer,
                backgroundImage: `url("${incomingPreviewUri}")`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
              } as ViewStyle,
              incomingPreviewStyle,
            ]}
          />
        ) : null}
      </View>
    </View>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  carousel: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 40,
    minHeight: 0,
  },
  features: {
    width: 386,
    flexShrink: 0,
    gap: 32,
  },
  feature: {
    gap: 16,
    cursor: 'pointer',
  },
  featureIconImage: {
    width: 40,
    height: 40,
  },
  featureIconBox: {
    backgroundColor:
      UnistylesRuntime.themeName === 'dark'
        ? theme.colors.background_3
        : '#F0F0F4',
  },
  featureInactive: {
    opacity: 0.4,
  },
  featureCopy: {
    gap: 16,
  },
  preview: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    borderRadius: 16,
    overflow: 'hidden',
  },
  previewLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
}));
